import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, CreditCard, Shield, ArrowLeft, Trash2 } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../contexts/CartContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const API_URL = process.env.REACT_APP_API_URL || '';

function CheckoutForm({ formData, setFormData, checkoutItems, total, handleSubmit, loading, handleRemoveItem }) {
  const stripe = useStripe();
  const elements = useElements();

  const onSubmit = (e) => handleSubmit(e, stripe, elements);

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <div>
        <label className="block text-xs font-bold text-gray-700 mb-1">Full Name</label>
        <input type="text" required value={formData.customerName}
          onChange={(e) => setFormData({...formData, customerName: e.target.value})}
          className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-pink-600 outline-none"
          placeholder="John Smith" />
      </div>
      <div>
        <label className="block text-xs font-bold text-gray-700 mb-1">Email</label>
        <input type="email" required value={formData.customerEmail}
          onChange={(e) => setFormData({...formData, customerEmail: e.target.value})}
          className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-pink-600 outline-none"
          placeholder="john@example.com" />
      </div>
      <div>
        <label className="block text-xs font-bold text-gray-700 mb-1">Phone <span className="font-normal text-gray-500">(optional)</span></label>
        <input type="tel" value={formData.customerPhone}
          onChange={(e) => setFormData({...formData, customerPhone: e.target.value})}
          className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-pink-600 outline-none"
          placeholder="+44 7700 900000" />
      </div>

      <div>
        <label className="block text-xs font-bold text-gray-700 mb-2">Payment Method</label>
        <div className="p-3 rounded-lg border-2 border-pink-600 bg-pink-50">
          <div className="flex items-center gap-2 mb-3">
            <CreditCard className="h-6 w-6 text-gray-600" />
            <div>
              <p className="font-bold text-sm text-gray-900">Credit / Debit Card</p>
              <p className="text-xs text-gray-600">Visa, Mastercard, Amex — instant and secure</p>
            </div>
          </div>
          <div className="px-3 py-2 border-2 border-gray-300 rounded-lg bg-white focus-within:border-pink-600">
            <CardElement options={{
              style: {
                base: {
                  fontSize: '14px',
                  color: '#374151',
                  '::placeholder': { color: '#9ca3af' }
                }
              }
            }} />
          </div>
        </div>
      </div>

      <button type="submit" disabled={loading || !stripe}
        className="w-full bg-gradient-to-r from-pink-600 to-purple-600 text-white py-3 rounded-lg font-bold text-base hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2">
        {loading ? 'Processing...' : (
          <><CreditCard className="w-5 h-5" />Pay Now — €{total.toFixed(2)}</>
        )}
      </button>

      <p className="text-center text-xs text-gray-500 mt-2">
        🔒 Secured by Stripe — your card data never touches our servers
      </p>
    </form>
  );
}

export default function Checkout() {
  const location = useLocation();
  const navigate = useNavigate();
  const orderData = location.state;
  const { clearCart } = useCart();

  const [loading, setLoading] = useState(false);
  const [checkoutItems, setCheckoutItems] = useState([]);

  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
  });

  useEffect(() => {
    if (!orderData) {
      navigate('/');
      return;
    }
    const isCartOrder = orderData.cartItems && Array.isArray(orderData.cartItems);
    setCheckoutItems(isCartOrder ? orderData.cartItems : [orderData]);
  }, [orderData, navigate]);

  const handleRemoveItem = (index) => {
    const newItems = checkoutItems.filter((_, i) => i !== index);
    setCheckoutItems(newItems);
    if (newItems.length === 0) {
      navigate('/');
    }
  };

  if (!orderData || checkoutItems.length === 0) return null;

  const total = checkoutItems.reduce((sum, item) => sum + parseFloat(item.price), 0);

  const getServiceLabel = (serviceType, platform) => {
    if (serviceType === 'followers') return platform === 'youtube' ? 'subscribers' : 'followers';
    return { likes: 'likes', views: 'views' }[serviceType] || 'followers';
  };

  const buildOrderPayload = () => {
    const base = {
      customerName: formData.customerName,
      customerEmail: formData.customerEmail,
      customerPhone: formData.customerPhone || null,
      paymentMethod: 'card',
    };
    if (checkoutItems.length > 1) {
      return { ...base, items: checkoutItems.map(item => ({
        platform: item.platform, username: item.username,
        serviceType: item.serviceType || 'followers', quantity: item.quantity,
        price: parseFloat(item.price), profilePic: item.profilePic,
        ...(item.selectedPosts && { selectedPosts: item.selectedPosts }),
      })), price: total };
    }
    const item = checkoutItems[0];
    const serviceType = item.serviceType || 'followers';
    return { ...base, platform: item.platform, username: item.username, serviceType,
      packageType: `${item.quantity} ${getServiceLabel(serviceType, item.platform)}`,
      quantity: item.quantity, followersCount: item.quantity, price: parseFloat(item.price),
      ...(item.selectedPosts && { selectedPosts: item.selectedPosts }) };
  };

  const handleSubmit = async (e, stripe, elements) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    try {
      const intentRes = await axios.post(`${API_URL}/payment/stripe/create-intent`, buildOrderPayload());
      if (!intentRes.data.success) throw new Error('Error starting payment');
      const { clientSecret } = intentRes.data;

      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: formData.customerName,
            email: formData.customerEmail,
          },
        },
      });

      if (error) {
        alert(error.message);
        setLoading(false);
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        clearCart();
        navigate('/order-success', {
          state: {
            orderCode: null,
            paymentInfo: { method: 'card' },
            total,
            customerEmail: formData.customerEmail,
          }
        });
      }
    } catch (error) {
      console.error('❌ Payment error:', error);
      alert('Error processing payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8 transition"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>

        <h1 className="text-4xl font-bold text-center mb-12">Checkout</h1>

        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
          {/* Order Summary */}
          <div>
            <div className="bg-white rounded-3xl p-8 shadow-lg mb-6">
              <h2 className="text-2xl font-bold mb-6">Order Summary</h2>

              <div className="space-y-4 mb-6">
                {checkoutItems.map((item, index) => {
                  const getServiceName = (serviceType, platform) => {
                    if (serviceType === 'followers') {
                      return platform === 'youtube' ? 'Subscribers' : 'Followers';
                    }
                    return { likes: 'Likes', views: 'Views' }[serviceType] || 'Followers';
                  };
                  const serviceName = getServiceName(item.serviceType || 'followers', item.platform);

                  return (
                    <div key={index} className="border-b pb-4 last:border-b-0 relative">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <div className="flex items-center gap-3">
                          <img
                            src={item.profilePic}
                            alt={item.username}
                            className="w-12 h-12 rounded-full border-2 border-pink-200"
                            referrerPolicy="no-referrer"
                          />
                          <div>
                            <p className="font-bold">@{item.displayName || item.username}</p>
                            <p className="text-xs text-gray-600 capitalize">{item.platform}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveItem(index)}
                          className="p-2 hover:bg-red-50 rounded-full transition group"
                          title="Remove item"
                        >
                          <Trash2 className="w-4 h-4 text-gray-400 group-hover:text-red-600 transition" />
                        </button>
                      </div>
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-semibold text-sm">
                            {item.quantity >= 1000
                              ? `${item.quantity / 1000}K`
                              : item.quantity}{' '}
                            {serviceName}
                          </p>
                          {item.selectedPosts && item.selectedPosts.length > 0 && (
                            <p className="text-xs text-gray-600">
                              {item.serviceType === 'likes'
                                ? `on ${item.selectedPosts.length} ${item.selectedPosts.length === 1 ? 'photo' : 'photos'}`
                                : `on ${item.selectedPosts.length} ${item.selectedPosts.length === 1 ? 'video' : 'videos'}`
                              }
                            </p>
                          )}
                        </div>
                        <p className="font-bold text-lg">€{parseFloat(item.price).toFixed(2)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-between items-center text-2xl font-bold pt-4 border-t">
                <span>Total:</span>
                <span className="text-pink-600">€{total.toFixed(2)}</span>
              </div>
            </div>

            <div className="hidden sm:block bg-green-50 rounded-2xl p-6 border border-green-200">
              <div className="flex items-center gap-3 text-green-700">
                <Shield className="w-6 h-6" />
                <div>
                  <p className="font-bold">100% Secure Payment</p>
                  <p className="text-sm">Your data is protected by Stripe</p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div>
            <div className="bg-white rounded-3xl p-4 sm:p-6 shadow-lg">
              <h2 className="text-xl font-bold mb-4">Your Details</h2>
              <Elements stripe={stripePromise}>
                <CheckoutForm
                  formData={formData}
                  setFormData={setFormData}
                  checkoutItems={checkoutItems}
                  total={total}
                  handleSubmit={handleSubmit}
                  loading={loading}
                  handleRemoveItem={handleRemoveItem}
                />
              </Elements>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
